import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Sparkles, Shield } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo83ProteccionRecaidas() {
    const [senalesTempranas, setSenalesTempranas] = useState('');
    const [escenarioRiesgo, setEscenarioRiesgo] = useState('');
    const [respuestaPreventiva, setRespuestaPreventiva] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [nivelRiesgo, setNivelRiesgo] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/proteccion_recaidas`, { withCredentials: true });
                if (data.data) {
                    if (data.data.senalesTempranas) setSenalesTempranas(data.data.senalesTempranas);
                    if (data.data.escenarioRiesgo) setEscenarioRiesgo(data.data.escenarioRiesgo);
                    if (data.data.respuestaPreventiva) setRespuestaPreventiva(data.data.respuestaPreventiva);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); }
        })();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'proteccion_recaidas', data: { senalesTempranas, escenarioRiesgo, respuestaPreventiva } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [senalesTempranas, escenarioRiesgo, respuestaPreventiva]);

    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [senalesTempranas, escenarioRiesgo, respuestaPreventiva, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && (senalesTempranas.trim().length > 0 && escenarioRiesgo.trim().length > 0 && respuestaPreventiva.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/proteccion-recaidas/submit`, {
                senales_tempranas: senalesTempranas, escenario_riesgo: escenarioRiesgo, respuesta_preventiva: respuestaPreventiva
            }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setNivelRiesgo(data.nivel_riesgo);
            setSubmitted(true);
            toast.success('Protección contra recaídas completada');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#7A9BA8] to-[#5F8494] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><Shield className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2" data-testid="proteccion-recaidas-success-title">Protección definida</h2>
                    <p className="text-[#7A9BA8] font-medium mb-4">Tu sistema de prevención está activo</p>
                    {nivelRiesgo && <div className="inline-block px-4 py-1.5 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-sm font-medium mb-4">Nivel de riesgo: {nivelRiesgo}</div>}
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#7A9BA8] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#7A9BA8] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="proteccion_recaidas" chapter={8} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="continue-after-proteccion-recaidas">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#EAE8E3]/50 sticky top-0 z-50">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/usuario" className="flex items-center gap-2 text-[#3E5F6E] hover:text-[#2F4A57]"><ArrowLeft className="h-5 w-5" /><span className="text-sm font-medium">Volver</span></Link>
                    <span className="text-xs text-[#9AA5B1]">{savingDraft ? 'Guardando...' : 'Borrador guardado'}</span>
                </div>
            </header>
            <main className="flex-1 max-w-3xl mx-auto px-4 py-8 w-full">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2"><div className="w-10 h-10 bg-[#3E5F6E] rounded-xl flex items-center justify-center"><Shield className="h-5 w-5 text-white" /></div><span className="text-xs font-semibold text-[#9AA5B1] uppercase tracking-wider">Capítulo 8 · Módulo 8.3</span></div>
                    <h1 className="text-2xl sm:text-3xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Protección contra recaídas</h1>
                    <p className="text-[#616E7C]">Prevé la escalada detectando riesgos temprano y definiendo una respuesta preventiva.</p>
                </div>
                <div className="space-y-6">
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Señales tempranas</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿Cuáles son las primeras señales de que estás a punto de recaer? (pensamientos, conductas, emociones)</p>
                        <Textarea value={senalesTempranas} onChange={e => setSenalesTempranas(e.target.value)} placeholder="Las primeras señales que noto son..." className="min-h-[100px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="proteccion-senales" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Escenario de riesgo</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">Describe el escenario más probable en el que podrías recaer.</p>
                        <Textarea value={escenarioRiesgo} onChange={e => setEscenarioRiesgo(e.target.value)} placeholder="El escenario de mayor riesgo es..." className="min-h-[100px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="proteccion-escenario" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Respuesta preventiva</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿Qué harás inmediatamente cuando detectes las señales?</p>
                        <Textarea value={respuestaPreventiva} onChange={e => setRespuestaPreventiva(e.target.value)} placeholder="Mi respuesta inmediata será..." className="min-h-[100px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="proteccion-respuesta" />
                    </div>
                    <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="w-full bg-[#3E5F6E] hover:bg-[#2F4A57] text-white py-3 rounded-xl" data-testid="submit-proteccion-recaidas">
                        {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analizando...</> : 'Completar protección'}
                    </Button>
                </div>
            </main>
            <SENDAFooter />
        </div>
    );
}
